import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTheme, API } from "@/App";
import axios from "axios";
import { Link2, ExternalLink, Moon, Sun } from "lucide-react";

const themes = {
  purple: { primary: "#7c3aed", gradient: "from-violet-500 to-purple-600" },
  blue: { primary: "#3b82f6", gradient: "from-blue-500 to-cyan-500" },
  green: { primary: "#10b981", gradient: "from-green-500 to-emerald-500" },
  pink: { primary: "#ec4899", gradient: "from-pink-500 to-rose-500" },
  orange: { primary: "#f59e0b", gradient: "from-orange-500 to-yellow-500" }
};

const PublicMicrosite = () => {
  const { slug } = useParams();
  const { theme, toggleTheme } = useTheme();
  const [microsite, setMicrosite] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMicrosite();
  }, [slug]);

  const fetchMicrosite = async () => {
    try {
      const response = await axios.get(`${API}/m/${slug}`);
      setMicrosite(response.data);
    } catch (error) {
      setError("Microsite tidak ditemukan");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !microsite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Link2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h1 className="text-2xl font-bold mb-2">404</h1>
          <p className="text-muted-foreground mb-6">{error || "Microsite tidak ditemukan"}</p>
          <Link to="/">
            <Button>Kembali ke Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const themeConfig = themes[microsite.theme] || themes.purple;

  return (
    <div 
      className="min-h-screen bg-background"
      style={{
        background: `linear-gradient(180deg, ${themeConfig.primary}15 0%, transparent 40%)`
      }}
    >
      {/* Theme Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={toggleTheme}
          className="bg-background/80 backdrop-blur-md"
          data-testid="public-theme-toggle"
        >
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>

      <div className="max-w-md mx-auto px-4 py-12" data-testid="public-microsite">
        {/* Avatar */}
        <div className="text-center mb-8">
          {microsite.avatar_url ? (
            <img
              src={microsite.avatar_url}
              alt={microsite.title}
              className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 shadow-xl"
              style={{ borderColor: themeConfig.primary }}
            />
          ) : (
            <div
              className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-bold text-white shadow-xl"
              style={{ backgroundColor: themeConfig.primary }}
            >
              {microsite.title.charAt(0).toUpperCase()}
            </div>
          )}

          <h1 
            className="text-2xl font-bold mb-2" 
            style={{ fontFamily: "Plus Jakarta Sans" }}
          >
            {microsite.title}
          </h1>

          {microsite.bio && (
            <p className="text-muted-foreground">{microsite.bio}</p>
          )}
        </div>

        {/* Links */}
        <div className="space-y-4">
          {microsite.links.map((link, index) => (
            <a
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block group"
              data-testid={`microsite-link-${index}`}
            >
              <div 
                className="p-4 rounded-2xl border-2 bg-card hover:scale-[1.02] transition-all duration-300 flex items-center justify-between"
                style={{ 
                  borderColor: `${themeConfig.primary}33`,
                  boxShadow: `0 4px 20px ${themeConfig.primary}10`
                }}
              >
                <span className="font-medium" style={{ fontFamily: "Plus Jakarta Sans" }}>
                  {link.title}
                </span>
                <ExternalLink 
                  className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform"
                  style={{ color: themeConfig.primary }}
                />
              </div>
            </a>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <div 
              className="w-6 h-6 rounded flex items-center justify-center"
              style={{ backgroundColor: themeConfig.primary }}
            >
              <Link2 className="w-4 h-4 text-white" />
            </div>
            <span>Dibuat dengan Campus Link</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PublicMicrosite;
